/**
 * «Отчёты» — `P2`–`P7`, те же, что «📊 Отчёты по оплатам» в боте (§12.2 ТЗ
 * `ops`). Текст отчёта собирает сервер (`reports.run_report`) — здесь только
 * выбор отчёта, периода, поставщика и порога. Параметры — в адресе, чтобы
 * отчёт можно было переслать ссылкой.
 */

import { useQuery } from '@tanstack/react-query';
import type { ApiError } from '@shell/api';
import { ErrorState, Loading } from '@shell/states';
import { navigate, useRoute } from '../../router';
import { isoDate, when } from '../../ui';
import { paymentsApi } from './api';
import type { Report, ReportList, ReportParams } from './api';

function readParams(params: URLSearchParams) {
  const supplier = Number(params.get('supplier_id'));
  return {
    code: params.get('code') ?? 'P2',
    from: params.get('from') ?? undefined,
    to: params.get('to') ?? undefined,
    supplier_id: supplier || undefined,
    min_total: params.get('min_total') ?? undefined,
  };
}

function reportPath(state: ReturnType<typeof readParams>): string {
  const params = new URLSearchParams({ code: state.code });
  if (state.from) params.set('from', state.from);
  if (state.to) params.set('to', state.to);
  if (state.supplier_id) params.set('supplier_id', String(state.supplier_id));
  if (state.min_total) params.set('min_total', state.min_total);
  return `/payments/reports?${params.toString()}`;
}

export function Reports() {
  const route = useRoute();
  const state = readParams(route.params);
  const set = (patch: Partial<typeof state>) =>
    navigate(reportPath({ ...state, ...patch }), { replace: true });

  const list = useQuery<ReportList, ApiError>({
    queryKey: ['payments', 'reports'],
    queryFn: () => paymentsApi.reports(),
  });

  const info = list.data?.reports.find((row) => row.code === state.code);
  const params: ReportParams = {
    from: info?.needs_period ? state.from : undefined,
    to: info?.needs_period ? state.to : undefined,
    supplier_id: info?.supplier ? state.supplier_id : undefined,
    min_total: info?.min_total ? state.min_total : undefined,
  };
  const report = useQuery<Report, ApiError>({
    queryKey: ['payments', 'report', state.code, params],
    queryFn: () => paymentsApi.report(state.code, params),
    enabled: Boolean(info),
    refetchOnWindowFocus: true,
  });

  if (list.isPending) return <Loading />;
  if (list.error) return <ErrorState error={list.error} onRetry={() => list.refetch()} />;

  return (
    <div className="pay-stack">
      <div className="chips" role="tablist" aria-label="Отчёты по оплатам">
        {list.data.reports.map((row) => (
          <button
            key={row.code}
            type="button"
            role="tab"
            aria-selected={row.code === state.code}
            className={`chip${row.code === state.code ? ' chip--active' : ''}`}
            onClick={() => set({ code: row.code })}
          >
            {row.title}
          </button>
        ))}
      </div>

      {info && (info.needs_period || info.supplier || info.min_total) && (
        <div className="pay-filters">
          {info.needs_period && (
            <>
              <label className="pay-field">
                <span>с</span>
                <input
                  className="input"
                  type="date"
                  value={state.from ?? ''}
                  onChange={(event) => set({ from: event.target.value || undefined })}
                />
              </label>
              <label className="pay-field">
                <span>по</span>
                <input
                  className="input"
                  type="date"
                  value={state.to ?? ''}
                  onChange={(event) => set({ to: event.target.value || undefined })}
                />
              </label>
            </>
          )}
          {info.supplier && (
            <label className="pay-field">
              <span>Поставщик</span>
              <select
                className="select"
                value={state.supplier_id ?? ''}
                onChange={(event) =>
                  set({ supplier_id: Number(event.target.value) || undefined })
                }
              >
                <option value="">все поставщики</option>
                {list.data.suppliers.map((supplier) => (
                  <option key={supplier.id} value={supplier.id}>
                    {supplier.name}
                  </option>
                ))}
              </select>
            </label>
          )}
          {info.min_total && (
            <label className="pay-field">
              <span>Порог, ₽</span>
              <input
                className="input"
                inputMode="decimal"
                placeholder="как в уведомлениях"
                defaultValue={state.min_total ?? ''}
                onBlur={(event) => set({ min_total: event.target.value.trim() || undefined })}
              />
            </label>
          )}
        </div>
      )}

      <section className="pay-card" aria-label="Отчёт">
        {!info ? (
          <p className="faint">Выберите отчёт.</p>
        ) : report.isPending ? (
          <Loading />
        ) : report.error ? (
          <ErrorState error={report.error} onRetry={() => report.refetch()} />
        ) : (
          <>
            <header className="pay-card__head">
              <h2 className="pay-card__title">{report.data.title}</h2>
              {report.data.period && (
                <span className="faint">
                  {isoDate(report.data.period.from)} — {isoDate(report.data.period.to)}
                </span>
              )}
            </header>
            <pre className="pay-report">{report.data.text}</pre>
            {report.data.synced_at && (
              <p className="faint">Данные StoreHouse от {when(report.data.synced_at)}.</p>
            )}
          </>
        )}
      </section>
    </div>
  );
}
